import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useForm, useFieldArray, useWatch, Controller } from "react-hook-form";
import { Plus, Save, X, ChevronLeft, ChevronRight, Minus } from "lucide-react";
import Input from "../../widgets/Input";
import groupsService from "../../services/groups.service";
import usersService from "../../services/users.service";
import toast from "react-hot-toast";
import CustomDropdownSelect from "../../widgets/CustomDropdownSelect";
import inviteusersService from "../../services/invite-users.service";
import Tooltip from "../../widgets/ToolTip";

// Interfaces
interface Group {
  group_key: string;
  group_name: string;
  roles: string[];
}

interface InviteUserFormData {
  family_name: string;
  given_name: string;
  email: string;
  mobile_no: string;
  groups: Group[];
}

// Optimized GroupRoleFields component
const GroupRoleFields = React.memo<{
  control: any;
  index: number;
  remove: (index: number) => void;
  departmentList: { value: string; label: string }[];
  totalFields: number;
  setValue: any;
  selectedDepartments: string[];
}>(
  ({
    control,
    index,
    remove,
    departmentList,
    totalFields,
    setValue,
    selectedDepartments,
  }) => {
    const [roleOptions, setRoleOptions] = useState<
      { value: string; label: string }[]
    >([]);
    const [isRolesLoading, setIsRolesLoading] = useState(false);

    const selectedGroup = useWatch({
      control,
      name: `groups.${index}.group_key`,
    });

    const selectedRoles = useWatch({
      control,
      name: `groups.${index}.roles`,
    });

    // Memoize available departments for this specific index
    const availableDepartments = useMemo(() => {
      return departmentList.filter(
        (option) =>
          !selectedDepartments.includes(option.value) ||
          option.value === selectedGroup
      );
    }, [departmentList, selectedDepartments, selectedGroup]);

    // Fetch roles when department changes
    useEffect(() => {
      let isCancelled = false;

      const fetchRoles = async () => {
        if (!selectedGroup) {
          setRoleOptions([]);
          return;
        }

        try {
          setIsRolesLoading(true);
          const response = await usersService.getMappedUsers(selectedGroup);

          if (!isCancelled) {
            const roles =
              response?.data?.map((r: any) => ({
                value: r.role_key,
                label: r.role_name,
              })) || [];
            setRoleOptions(roles);
          }
        } catch (err) {
          if (!isCancelled) {
            console.error("Error fetching roles:", err);
            setRoleOptions([]);
          }
        } finally {
          if (!isCancelled) {
            setIsRolesLoading(false);
          }
        }
      };

      fetchRoles();

      return () => {
        isCancelled = true;
      };
    }, [selectedGroup]);

    const handleDepartmentChange = useCallback(
      (option: any) => {
        setValue(`groups.${index}.group_key`, option ? option.value : "");
        setValue(`groups.${index}.group_name`, option ? option.label : "");
        setValue(`groups.${index}.roles`, []); // Clear roles when department changes
      },
      [setValue, index]
    );

    const handleRoleChange = useCallback(
      (options: any) => {
        setValue(
          `groups.${index}.roles`,
          options ? options.map((o: any) => o.value) : []
        );
      },
      [setValue, index]
    );

    return (
      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-4">
        <div className="flex items-start gap-4">
          {/* Group Dropdown */}
          <div className="flex-1">
            <Controller
              name={`groups.${index}.group_key`}
              control={control}
              rules={{ required: "Group is required" }}
              render={({ field}) => {
                const selectedOption =
                  availableDepartments.find(
                    (option) => option.value === field.value
                  ) || null;

                return (
                  <div>
                    <CustomDropdownSelect
                      label="Group Department"
                      name={field.name}
                      control={control}
                      options={availableDepartments}
                      value={selectedOption}
                      placeholder="Group department"
                      onSelectionChange={handleDepartmentChange}
                    />
                  </div>
                );
              }}
            />
          </div>

          {/* Roles Dropdown */}
          <div className="flex-1">
            <Controller
              name={`groups.${index}.roles`}
              control={control}
              rules={{ required: "At least one role is required" }}
              render={({ field }) => {
                const selectedRoleOptions =
                  roleOptions.filter((role) =>
                    selectedRoles?.includes(role.value)
                  ) || [];

                return (
                  <div>
                    <CustomDropdownSelect
                      isMulti
                      name={field.name}
                      label="Select Roles"
                      control={control}
                      placeholder={
                        isRolesLoading ? "Loading roles..." : "Select roles"
                      }
                      options={roleOptions}
                      value={selectedRoleOptions}
                      disabled={!selectedGroup || isRolesLoading}
                      onSelectionChange={handleRoleChange}
                    />
                  </div>
                );
              }}
            />
          </div>

          {/* Remove Button */}
          {totalFields > 1 && (
            <button
              type="button"
              onClick={() => remove(index)}
             className="p-2 mb-2 text-red-600 hover:text-red-700 mt-6"
            >
            <Tooltip content="Remove">
              <Minus className="w-5 h-5" />
            </Tooltip>
            </button>
          )}
        </div>
      </div>
    );
  }
);

GroupRoleFields.displayName = "GroupRoleFields";

// Modal Component
interface InviteUserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRefresh: () => void;
}

const InviteUserFormModal: React.FC<InviteUserFormModalProps> = ({
  isOpen,
  onClose,
  onRefresh,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [departmentList, setDepartmentList] = useState<
    { value: string; label: string }[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isValidating },
    trigger,
    clearErrors,
    getValues,
  } = useForm<InviteUserFormData>({
    mode: "onChange", // Changed to onChange for better UX
    defaultValues: {
      family_name: "",
      given_name: "",
      email: "",
      mobile_no: "",
      groups: [{ group_key: "", group_name: "", roles: [] }],
    },
  });

  const {
    fields: groupFields,
    append: addGroup,
    remove: removeGroup,
  } = useFieldArray({
    control,
    name: "groups",
  });

  const steps = [
    {
      title: "Basic Information",
      fields: ["family_name", "given_name", "email", "mobile_no"] as const,
      description: "Enter user's basic details",
    },
    {
      title: "Groups & Role",
      fields: ["groups"] as const,
      description: "Assign groups and roles",
    },
  ];

  // Watch selected departments with proper memoization
  const watchedGroups = useWatch({ control, name: "groups" });
  const selectedDepartments = useMemo(() => {
    return watchedGroups?.map((g) => g.group_key).filter(Boolean) || [];
  }, [watchedGroups]);

  // Fetch departments on modal open
  useEffect(() => {
    if (!isOpen) return;

    const fetchDepartments = async () => {
      try {
        const response = await groupsService.getGroupsList();
        const departments =
          response?.data?.map((item: any) => ({
            value: item.group_id,
            label: item.group_name,
          })) || [];
        setDepartmentList(departments);
      } catch (err) {
        console.error("Error fetching groups:", err);
        toast.error("Failed to load departments");
      }
    };
    fetchDepartments();
  }, [isOpen]);

  // Handle step navigation with better validation
  const handleNext = useCallback(async () => {
    const currentStepFields = steps[currentStep].fields;

    try {
      const isValid = await trigger(currentStepFields);

      if (isValid) {
        setCurrentStep((prev) => prev + 1);
        // Clear any existing errors for the next step
        if (currentStep + 1 < steps.length) {
          clearErrors(steps[currentStep + 1].fields);
        }
      } else {
        // Get current form values to show specific error messages
        const values = getValues();
        let missingFields: string[] = [];

        if (currentStep === 0) {
          if (!values.family_name) missingFields.push("First Name");
          if (!values.given_name) missingFields.push("Last Name");
          if (!values.email) missingFields.push("Email");
        } else if (currentStep === 1) {
          const hasValidGroup = values.groups?.some(
            (g) => g.group_key && g.roles.length > 0
          );
          if (!hasValidGroup)
            missingFields.push("At least one Department and Role");
        }

        if (missingFields.length > 0) {
          toast.error("Please fill in all required fields");
        }
      }
    } catch (error) {
      console.error("Validation error:", error);
      toast.error(
        error.response.data.error.error_description ||
          "Please check all required fields"
      );
    }
  }, [currentStep, trigger, clearErrors, steps, getValues]);

  const handlePrevious = useCallback(() => {
    setCurrentStep((prev) => Math.max(0, prev - 1));
  }, []);

  // Add department with validation
  const handleAddDepartment = useCallback(() => {
    const availableCount = departmentList.length - selectedDepartments.length;

    if (availableCount > 0) {
      addGroup({ group_key: "", group_name: "", roles: [] });
    } else {
      toast.error("All departments have been selected");
    }
  }, [departmentList.length, selectedDepartments.length, addGroup]);

  // Handle form submission
  const onSubmit = useCallback(async (data: InviteUserFormData) => {
    try {
      setIsLoading(true);

      // Validate that we have at least one valid group
      const validGroups = data.groups.filter(
        (g) => g.group_key && g.roles.length > 0
      );

      if (validGroups.length === 0) {
        toast.error("Please select at least one department and role");
        return;
      }

      const transformedPayload = {
        email: data.email,
        given_name: data.given_name,
        family_name: data.family_name,
        mobile_no: data.mobile_no,
        groups: validGroups.map((group) => ({
          group_id: group.group_key,
          roles: group.roles.map((roleKey) => ({
            role_key: roleKey,
          })),
        })),
      };

      console.log("Submitting payload:", transformedPayload);

      await inviteusersService.inviteUser(transformedPayload);
      toast.success("User invited successfully!");
      onRefresh();
    } catch (err) {
      console.error("Error inviting user:", err);
      toast.error(
        err.response.data.error.error_description || "Failed to invite user"
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleModalClose = useCallback(() => {
    setCurrentStep(0);
    reset();
    clearErrors();
    onClose();
  }, [reset, clearErrors, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          // onClick={handleModalClose}
        />

        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] h-[68vh] flex flex-col">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Invite User
              </h2>

              {/* Stepper */}
              <div className="flex items-center space-x-2">
                {steps.map((step, index) => (
                  <React.Fragment key={index}>
                    <div className="flex items-center">
                      <div
                        className={`flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                          index <= currentStep
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {index + 1}
                      </div>
                      <div className="ml-2">
                        <span
                          className={`text-sm font-medium ${
                            index <= currentStep
                              ? "text-blue-600"
                              : "text-gray-500"
                          }`}
                        >
                          {step.title}
                        </span>
                        <p className="text-xs text-gray-400">
                          {step.description}
                        </p>
                      </div>
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={`w-8 h-0.5 mx-2 transition-colors ${
                          index < currentStep ? "bg-blue-600" : "bg-gray-200"
                        }`}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <button
              onClick={handleModalClose}
              type="button"
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            >
              <Tooltip content="Close">
                <X className="w-6 h-6" />
              </Tooltip>
            </button>
          </div>

          {/* Modal Body */}
          <div className="p-6 flex-1 overflow-y-auto">
            <form className="space-y-6 h-full">
              {/* Step 1: Basic Information */}
              {currentStep === 0 && (
                <div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Basic Information
                    </h3>
                    <p className="text-gray-600 text-sm mb-6">
                      Enter the user's personal details
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                      name="given_name"
                      control={control}
                      label="First Name"
                      placeholder="Enter first name"
                      rules={{ 
                        required: "First name is required",
                        pattern: {
                          value: /^[A-Za-z0-9\s]*$/,
                          message: "Only alphabets, numbers, and spaces are allowed"
                        }
                      }}
                    />
                    <Input
                      name="family_name"
                      control={control}
                      label="Last Name"
                      placeholder="Enter last name"
                      rules={{ 
                        required: "Last name is required",
                        pattern: {
                          value: /^[A-Za-z0-9\s]*$/,
                          message: "Only alphabets, numbers, and spaces are allowed"
                        }
                      }}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                      name="email"
                      control={control}
                      label="Email"
                      placeholder="Enter email address"
                      rules={{
                        required: "Email is required",
                        pattern: {
                          value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                          message: "Invalid email address",
                        },
                      }}
                    />
                    <Input
                      name="mobile_no"
                      control={control}
                      label="Mobile Number"
                      placeholder="Enter mobile number"
                      type="number"
                      maxLength={10}
                      rules={{
                        pattern: {
                          value: /^[0-9]{10}$/,
                          message: "Mobile number must be 10 digits",
                        },
                      }}
                    />
                  </div>

                  {/* Inline Navigation for Step 1 */}
                  <div className="flex justify-end pt-[80px]">
                    <button
                      type="button"
                      onClick={handleNext}
                      disabled={isValidating}
                      className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      {isValidating ? "Validating..." : "Next"}
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Departments & Roles */}
              {currentStep === 1 && (
                <div className="space-y-6 h-full flex flex-col">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        Groups & Roles{" "}
                        <span className="text-red-600">*</span>
                      </h3>
                      <p className="text-gray-600 text-sm mt-1">
                        Assign user to Groups and roles
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddDepartment}
                      disabled={
                        selectedDepartments.length >= departmentList.length
                      }
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedDepartments.length >= departmentList.length
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-blue-600 text-white hover:bg-blue-700"
                      }`}
                    >
                      <Plus className="w-4 h-4" />
                      Add
                    </button>
                  </div>

                  <div className="flex-1 space-y-4 pr-2 pb-4">
                    {groupFields.map((field, index) => (
                      <GroupRoleFields
                        key={field.id}
                        control={control}
                        index={index}
                        remove={removeGroup}
                        departmentList={departmentList}
                        totalFields={groupFields.length}
                        setValue={setValue}
                        selectedDepartments={selectedDepartments.filter(
                          (_, i) => i !== index
                        )}
                      />
                    ))}
                  </div>

                  {groupFields.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <p>No group assigned yet.</p>
                      <p className="text-sm">
                        Click "Add Group" to get started.
                      </p>
                    </div>
                  )}

                  {/* Inline Navigation for Step 2 */}
                  <div className="flex items-center justify-between pt-6 border-t border-gray-200 bg-white">
                    <div className="flex items-center space-x-3">
                      <button
                        type="button"
                        onClick={handlePrevious}
                        className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Previous
                      </button>
                    </div>

                    <button
                      type="submit"
                      onClick={handleSubmit(onSubmit)}
                      disabled={isLoading}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors duration-150"

                    >
                      <Save className="w-4 h-4" />
                      {isLoading ? "Sending..." : "Send Invitation"}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteUserFormModal;
